import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import { ENV_CONFIG } from '@ps/core-config';
import { NotificationItem } from '@ps/notification-models';

import { NotificationMapper } from './mappers/notification.mapper';
import {
  MarkViewedPayloadDto,
  NotificationDto,
  UnseenCountDto,
  ViewAllResultDto,
} from './dtos/notification.dto';
import { NotificationUnreadCounts } from '../state/notification.model';

@Injectable({ providedIn: 'root' })
export class NotificationApi {
  private readonly _environmentConfig = inject(ENV_CONFIG);
  private readonly _httpClient = inject(HttpClient);
  private readonly _notificationMapper = new NotificationMapper();

  private readonly _baseUrl = `${this._environmentConfig.pureSpectrum.gatewayUrl}/v1/notifications`;

  public notifications(limit: number): Observable<NotificationItem[]> {
    return this._httpClient
      .get<NotificationDto[]>(this._baseUrl, {
        params: new HttpParams().set('limit', limit),
      })
      .pipe(
        map((notificationDtos) =>
          this._notificationMapper.fromDtoList(notificationDtos),
        ),
      );
  }

  public unreadCounts(): Observable<NotificationUnreadCounts> {
    return this._httpClient
      .get<UnseenCountDto>(`${this._baseUrl}/unread-count`)
      .pipe(
        map((unseenCountDto) => ({
          unseenCount: unseenCountDto.unseenCount,
          communicationCount: unseenCountDto.communicationCount ?? null,
          needsActionCount: unseenCountDto.needsActionCount ?? null,
        })),
      );
  }

  public viewAll(): Observable<number> {
    return this._httpClient
      .patch<ViewAllResultDto>(`${this._baseUrl}/view-all`, {})
      .pipe(map((viewAllResultDto) => viewAllResultDto.changed));
  }

  public markViewed(
    notificationId: string,
    viewed: boolean,
  ): Observable<NotificationItem> {
    const payload: MarkViewedPayloadDto = { viewed };

    return this._httpClient
      .patch<NotificationDto>(
        `${this._baseUrl}/${notificationId}/view`,
        payload,
      )
      .pipe(
        map((notificationDto) =>
          this._notificationMapper.fromDto(notificationDto),
        ),
      );
  }
}
